import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import './MyCourses.css';

function MyCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, completed

  // TODO: Get actual userId from authentication context/token
  const userId = 1; // Placeholder - replace with actual user ID

  useEffect(() => {
    fetchMyCourses();
  }, []);

  const fetchMyCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BASE_URL}/enrollments/user/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch courses: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setCourses(result.data);
      } else {
        throw new Error('Failed to load courses');
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartCourse = (courseId) => {
    navigate(`/player?courseId=${courseId}`);
  };

  const getFilteredCourses = () => {
    if (filter === 'all') return courses;
    if (filter === 'active') return courses.filter(c => c.status === 'active');
    if (filter === 'completed') return courses.filter(c => c.status === 'completed');
    return courses;
  };

  const getLevelBadgeColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'beginner': return '#4CAF50';
      case 'intermediate': return '#FF9800';
      case 'advanced': return '#f44336';
      default: return '#2196F3';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return '#2196F3';
      case 'completed': return '#4CAF50';
      case 'dropped': return '#9E9E9E';
      case 'suspended': return '#f44336';
      default: return '#9E9E9E';
    }
  };

  const filteredCourses = getFilteredCourses();

  if (loading) {
    return (
      <div className="my-courses-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-courses-container">
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <h3>Error Loading Courses</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={fetchMyCourses}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="my-courses-container">
      <div className="my-courses-header">
        <div className="header-content">
          <h2>My Courses</h2>
          <p className="subtitle">
            {courses.length} {courses.length === 1 ? 'course' : 'courses'} enrolled
          </p>
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({courses.length})
          </button>
          <button
            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active ({courses.filter(c => c.status === 'active').length})
          </button>
          <button
            className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed ({courses.filter(c => c.status === 'completed').length})
          </button>
        </div>
      </div>

      {filteredCourses.length === 0 ? (
        <div className="no-courses">
          <div className="no-courses-icon">📚</div>
          <h3>No courses found</h3>
          <p>
            {filter === 'all'
              ? "You haven't enrolled in any courses yet."
              : `You don't have any ${filter} courses.`}
          </p>
          <button className="browse-courses-btn">Browse Course Catalog</button>
        </div>
      ) : (
        <div className="courses-grid">
          {filteredCourses.map((course) => (
            <div key={course.id} className="course-tile">
              <div className="course-thumbnail">
                {course.thumbnail_url ? (
                  <img src={course.thumbnail_url} alt={course.course_title} />
                ) : (
                  <div className="thumbnail-placeholder">
                    <span className="placeholder-icon">📚</span>
                  </div>
                )}
                <div className="course-badges">
                  <span
                    className="badge level-badge"
                    style={{ backgroundColor: getLevelBadgeColor(course.level) }}
                  >
                    {course.level || 'N/A'}
                  </span>
                  <span
                    className="badge status-badge"
                    style={{ backgroundColor: getStatusBadgeColor(course.status) }}
                  >
                    {course.status}
                  </span>
                </div>
              </div>

              <div className="course-content">
                <div className="course-header">
                  <h3 className="course-title">{course.course_title}</h3>
                  <span className="course-code">{course.course_code}</span>
                </div>

                {course.short_description && (
                  <p className="course-description">{course.short_description}</p>
                )}

                <div className="course-meta">
                  {course.category && (
                    <span className="meta-item">
                      <span className="meta-icon">🏷️</span>
                      {course.category}
                    </span>
                  )}
                  {course.duration_hours && (
                    <span className="meta-item">
                      <span className="meta-icon">⏱️</span>
                      {course.duration_hours}h
                    </span>
                  )}
                  {course.language && (
                    <span className="meta-item">
                      <span className="meta-icon">🌐</span>
                      {course.language}
                    </span>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="progress-section">
                  <div className="progress-header">
                    <span className="progress-label">Progress</span>
                    <span className="progress-percentage">
                      {course.progress_percentage || 0}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${course.progress_percentage || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Enrollment Info */}
                <div className="enrollment-info">
                  <div className="info-item">
                    <span className="info-label">Enrolled:</span>
                    <span className="info-value">
                      {new Date(course.enrollment_date).toLocaleDateString()}
                    </span>
                  </div>
                  {course.last_accessed_at && (
                    <div className="info-item">
                      <span className="info-label">Last accessed:</span>
                      <span className="info-value">
                        {new Date(course.last_accessed_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {course.completed_at && (
                    <div className="info-item completed">
                      <span className="info-label">✓ Completed:</span>
                      <span className="info-value">
                        {new Date(course.completed_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Certificate */}
                {course.certificate_issued_at && course.certificate_url && (
                  <div className="certificate-section">
                    <span className="certificate-icon">🎓</span>
                    <a
                      href={course.certificate_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="certificate-link"
                    >
                      View Certificate
                    </a>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="course-actions">
                  {course.status === 'completed' ? (
                    <button 
                      className="action-btn review-btn"
                      onClick={() => handleStartCourse(course.course_id)}
                    >
                      Review Course
                    </button>
                  ) : (
                    <button 
                      className="action-btn continue-btn"
                      onClick={() => handleStartCourse(course.course_id)}
                    >
                      {course.progress_percentage > 0 ? 'Continue Learning' : 'Start Course'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyCourses;
